import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Table } from 'primeng/table'
import { BehaviorSubject, Observable, of, forkJoin } from 'rxjs'
import { debounceTime, distinctUntilChanged, map, switchMap, filter, take } from 'rxjs/operators'
import { NewRoleModalComponent } from '../new-role-modal/new-role-modal.component'
import { EditRoleModalComponent } from '../edit-role-modal/edit-role-modal.component'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { UiService } from 'src/app/services/ui.service'
import { AccountService } from '../../services/account.service'
import { ApiService } from 'src/app/services/api.service'
import { RoleDto, ApplicationDto, RolePolicyDto } from 'src/app/services/dtos'
import { AccessLevelService, AccessLevel, AccountPermission } from 'src/app/services/access-level.service'
import { SidepanelService, SidepanelController } from '../../services/sidepanel.service'
import { DateFormattingService } from 'src/app/services/date-formatting.service'
import { handleArpioError } from 'src/utils/handle-error'

export interface RoleManagementItem {
  id: string
  name: string
  externalId?: string
  accessLevels: AccessLevelInfo[] // All unique access levels with their applications
  applications: string[]
  applicationCount?: number // Actual count for display, separate from applications array
  policyCount: number
  createdAt: Date
  modifiedAt: Date
  isSystemRole?: boolean
  policies?: RolePolicyDto[] // Original policy data to avoid API calls
}

export interface AccessLevelInfo {
  accessLevel: AccessLevel
  applications: string[]
}

export interface PolicyInfo {
  accessLevel: string
  allApps: boolean
  appIds: string[] | null
  appNames?: string[] // For display purposes
  policyName?: string // Generated policy name for display
}

// Helper function to convert RoleDto to RoleManagementItem
function convertRoleDto(role: RoleDto, applications: ApplicationDto[]): RoleManagementItem {
  // Extract application names from policies
  const allAppIds = new Set<string>()
  let hasAllApps = false

  // Check policies for application access
  role.policies?.forEach((policy) => {
    if (policy.allApps) {
      hasAllApps = true
    }
    policy.appIds?.forEach((appId) => allAppIds.add(appId))
  })

  let appNames: string[]
  let actualAppCount: number

  if (hasAllApps) {
    // Roles with allApps show "All Applications"
    appNames = ['All Applications']
    actualAppCount = applications.length // Show the actual count of all applications
  } else {
    // For specific app assignments
    appNames = Array.from(allAppIds)
      .map((appId) => {
        const app = applications.find((a) => a.appId === appId)
        return app ? app.name : appId
      })
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    actualAppCount = appNames.length // Count of specific apps
  }

  // Calculate all unique access levels with their associated applications
  const accessLevelMap = new Map<AccessLevel, Set<string>>()

  role.policies?.forEach((policy) => {
    const level = policy.accessLevel as AccessLevel
    if (!accessLevelMap.has(level)) {
      accessLevelMap.set(level, new Set<string>())
    }

    if (policy.allApps) {
      // Add all applications
      applications.forEach((app) => accessLevelMap.get(level)!.add(app.name))
    } else {
      // Add specific applications
      policy.appIds?.forEach((appId) => {
        const app = applications.find((a) => a.appId === appId)
        if (app) {
          accessLevelMap.get(level)!.add(app.name)
        }
      })
    }
  })

  // Convert to AccessLevelInfo array
  const accessLevelInfos: AccessLevelInfo[] = Array.from(accessLevelMap.entries()).map(([level, apps]) => ({
    accessLevel: level,
    applications: Array.from(apps).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())),
  }))

  return {
    id: role.roleId || '',
    name: role.name || '',
    externalId: role.externalId,
    accessLevels: accessLevelInfos,
    applications: appNames,
    applicationCount: actualAppCount,
    policyCount: role.policies?.length || 0,
    createdAt: role.createdAt || new Date(),
    modifiedAt: role.lastUpdatedAt || new Date(),
    isSystemRole: role.isSystemRole || false,
    policies: role.policies, // Include original policy data to avoid API calls
  }
}

// Helper function to generate badge tooltip for access levels
function generateAccessLevelTooltip(applications: string[], prefix: string = ''): string {
  if (!applications || applications.length === 0) return 'All Applications'

  const badgeHtml = applications.map((name) => `<span class="tooltip-badge">${name}</span>`).join('')
  return `${prefix} ${badgeHtml}`
}

@Component({
  selector: 'app-role-management',
  templateUrl: './role-management.component.html',
  host: { class: 'rbac-management-page' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RoleManagementComponent implements OnInit, OnDestroy, SidepanelController {
  roles: RoleManagementItem[] = null
  isDataLoaded = false
  selectedRole: RoleManagementItem | null = null
  selectedRoleDetails: RoleDto | null = null // Full role data with policies
  selectedTableItem: RoleManagementItem | null = null // For table selection binding
  showSidePanel = false
  refreshingRoleIds: Set<string> = new Set()

  filterText = ''
  isFilterApplied = false
  loadingFilterInput = false
  debounceSubject = new BehaviorSubject<string>('')

  @ViewChild('dt1') rolesTable: Table
  @ViewChild('filterInput') filterInput!: ElementRef
  @ViewChild('newRoleModal') newRoleModal: NewRoleModalComponent
  @ViewChild('editRoleModal') editRoleModal: EditRoleModalComponent
  @ViewChild('deleteRoleDialog', { static: true }) deleteRoleDialog: ModalDialogComponent
  @ViewChild('sidePanel') sidePanel: ElementRef

  applications: ApplicationDto[] = []

  // Permission properties
  canManageRoles$: Observable<boolean>
  canViewRoles$: Observable<boolean>
  unauthorizedMessage$: Observable<string>

  // Delete role dialog
  roleToDelete: RoleManagementItem | null = null

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public uiService: UiService,
    public accountService: AccountService,
    private apiService: ApiService,
    private accessLevelService: AccessLevelService,
    private sidepanelService: SidepanelService,
    private dateFormattingService: DateFormattingService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Initialize permission observables
    // canManageRoles requires both MANAGE_RBAC permission AND rbacFullEnabled
    this.canManageRoles$ = this.accessLevelService
      .hasAccountPermission(AccountPermission.MANAGE_RBAC)
      .pipe(map((hasPermission) => hasPermission && this.accountService.rbacFullEnabled))
    this.canViewRoles$ = this.accessLevelService.hasAccountPermission(AccountPermission.READ_ROLE)
    this.unauthorizedMessage$ = of(this.accessLevelService.getUnauthorizedMessage(AccountPermission.READ_ROLE))

    // Redirect to account root if user doesn't have view permission
    this.canViewRoles$.subscribe((value) => {
      if (!value) {
        this.router.navigate([`/${this.accountService.accountId}`])
      }
    })

    this.setupDebounceFilter()
    this.checkForRoleSelection()

    // Wait for account to be loaded before loading data
    // This prevents the component from trying to load data before accountId is available
    this.accountService.accountIdStream.pipe(take(1)).subscribe(() => {
      this.loadData()
    })

    // Register with sidepanel service
    this.sidepanelService.register(this, this)
  }

  private loadData(onComplete?: () => void): void {
    this.isDataLoaded = false
    // Wait for accountId to be available before making API calls
    this.accountService.accountIdStream
      .pipe(
        filter((accountId) => !!accountId),
        take(1),
        switchMap((accountId) =>
          forkJoin({
            applications: this.apiService.listApplications(accountId),
            roles: this.apiService.listRoles(accountId),
          })
        )
      )
      .subscribe({
        next: (data) => {
          // Use the loaded application DTOs directly
          this.applications = data.applications || []

          // Process roles with loaded applications
          let processedRoles = (data.roles || []).map((dto) => convertRoleDto(dto, this.applications))

          // Show all roles - create/edit buttons are controlled by canManageRoles$ which checks rbacFullEnabled
          // This allows users to preview available roles even without full RBAC access (teaser UX)
          this.roles = processedRoles.sort(this.sortRolesWithAccountRolesFirst)

          this.refreshingRoleIds.clear()
          this.refreshRolePolicyAppNames()

          // Update selected role reference if it exists
          if (this.selectedRole) {
            const updatedRole = this.roles.find((r) => r.id === this.selectedRole.id)
            if (updatedRole) {
              this.selectedRole = updatedRole
              this.selectedTableItem = updatedRole
            }
          }
          this.cdr.detectChanges()

          // Call completion callback if provided
          if (onComplete) {
            onComplete()
          }
          this.isDataLoaded = true
        },
        error: (error) => {
          console.error('Error loading data:', error)
          this.applications = []
          this.roles = []
          this.refreshingRoleIds.clear()
          this.isDataLoaded = true
          this.cdr.detectChanges()
        },
      })
  }

  private checkForRoleSelection() {
    this.route.queryParams.subscribe((params) => {
      const roleToSelect = params['selectRole']
      if (roleToSelect) {
        this.selectRoleWhenLoaded(roleToSelect)
      }
    })
  }

  private selectRoleWhenLoaded(roleName: string) {
    if (this.roles && this.rolesTable) {
      const role = this.roles.find((r) => r.name === roleName)
      if (role) {
        this.selectedRole = role
        this.selectedTableItem = role
        // Also set the table selection to trigger CSS styling
        this.rolesTable.selection.set(role)
        this.openSidePanel()
      }
    } else {
      // Retry after data loads
      setTimeout(() => this.selectRoleWhenLoaded(roleName), 100)
    }
  }

  ngOnDestroy() {
    // Cleanup subscriptions if any
    // Unregister from sidepanel service
    this.sidepanelService.unregister(this)
  }

  private setupDebounceFilter() {
    this.debounceSubject.pipe(debounceTime(600), distinctUntilChanged()).subscribe((filterText: string) => {
      this.applyFilter(filterText)
      this.loadingFilterInput = false
    })
  }

  /**
   * Sort roles to show account-level roles first, then application-level roles
   * This sorting will remain even after the temporary role creation is removed
   */
  private sortRolesWithAccountRolesFirst = (a: RoleManagementItem, b: RoleManagementItem): number => {
    // System roles always first
    if (a.isSystemRole && !b.isSystemRole) return -1
    if (!a.isSystemRole && b.isSystemRole) return 1

    // Then sort by access level hierarchy (account levels first)
    const hasAccountLevelA = a.accessLevels.some((level) =>
      this.accessLevelService.isAccountLevelAccess(level.accessLevel)
    )
    const hasAccountLevelB = b.accessLevels.some((level) =>
      this.accessLevelService.isAccountLevelAccess(level.accessLevel)
    )

    if (hasAccountLevelA && !hasAccountLevelB) return -1
    if (!hasAccountLevelA && hasAccountLevelB) return 1

    // Within the same category, sort alphabetically by name (case-insensitive)
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  }

  onRowSelect(role: RoleManagementItem) {
    this.selectedRole = role
    this.selectedTableItem = role
    this.openSidePanel()
  }

  private openSidePanel() {
    if (this.selectedRole) {
      this.showSidePanel = true
      this.selectedRoleDetails = null

      // Load full role details including policies when sidepanel opens
      this.loadRoleDetails()
    }
  }

  private loadRoleDetails() {
    if (!this.selectedRole) return

    this.apiService.getRole(this.accountService.accountId, this.selectedRole.id).subscribe(
      (roleDto) => {
        this.selectedRoleDetails = roleDto
        // Refresh app names immediately since applications should already be loaded
        this.refreshRolePolicyAppNames()
      },
      (error) => {
        console.error('Failed to load role details:', error)
        this.selectedRoleDetails = null
      }
    )
  }

  closeSidePanel() {
    this.showSidePanel = false
    this.selectedRole = null
    this.selectedRoleDetails = null
    this.selectedTableItem = null // Clear the table selection binding
  }

  filterGlobal(event: any) {
    this.loadingFilterInput = true
    const filterText = event.target.value
    this.debounceSubject.next(filterText)
  }

  applyFilter(filterText: string) {
    if (!this.rolesTable || typeof filterText !== 'string') return

    const filterValue = filterText.toLowerCase().trim()

    if (!filterValue) {
      this.rolesTable.clear()
      this.filterText = filterValue
      this.isFilterApplied = false
      return
    }

    this.rolesTable.filterGlobal(filterValue, 'contains')
    this.filterText = filterValue
    this.isFilterApplied = true
  }

  clearInput() {
    this.filterText = ''
    this.filterInput.nativeElement.value = ''
    this.isFilterApplied = false
    this.rolesTable.clear()
  }

  /**
   * Check if a role is a default account-level role that should have special styling
   */
  isDefaultAccountRole(role: RoleManagementItem): boolean {
    return this.accessLevelService.isDefaultAccountRole(role)
  }

  openNewRoleModal() {
    this.newRoleModal.open()
  }

  openEditRoleModal() {
    if (this.selectedRole) {
      this.editRoleModal.open(this.selectedRole)
    }
  }

  onRoleCreated() {
    // Refresh the roles data when a custom role is created
    this.closeSidePanel()
    this.loadData()
  }

  onRoleUpdated(role: RoleManagementItem) {
    // Add the role to the refreshing set to show spinner
    this.refreshingRoleIds.add(role.id)
    // Keep track of which role was selected before refresh
    const wasRoleSelected = this.selectedRole?.id === role.id
    // Refresh the roles data when a role is updated
    this.loadData(() => {
      // If the same role was selected, reopen the side panel with updated data
      if (wasRoleSelected && this.selectedRole) {
        this.openSidePanel()
      }
    })
  }

  isRoleRefreshing(role: RoleManagementItem): boolean {
    return this.refreshingRoleIds.has(role.id)
  }

  roleTrackBy(_index: number, role: RoleManagementItem) {
    return role.id
  }

  getAccessLevelClass(accessLevel: AccessLevel): string {
    return `access-level-tag ${accessLevel}`
  }

  getAccessLevelDisplay(accessLevel: AccessLevel | string): string {
    return this.accessLevelService.getAccessLevelDisplayName(accessLevel as any)
  }

  getLastModifiedDisplay(modifiedAt: Date | null): string {
    return this.dateFormattingService.getRelativeTimeDisplay(modifiedAt, 'Never')
  }

  getLastModifiedTooltip(modifiedAt: Date | null): string | null {
    return this.dateFormattingService.getRelativeTimeTooltip(modifiedAt)
  }

  get globalFilterFields() {
    return ['name', 'externalId']
  }

  get rbacFullEnabled(): boolean {
    return this.accountService.rbacFullEnabled
  }

  customSort(event: any) {
    // Handle different field types with custom sorting logic
    const field = event.field
    const order = event.order // 1 for ascending, -1 for descending

    event.data.sort((a: RoleManagementItem, b: RoleManagementItem) => {
      switch (field) {
        case 'externalId':
          // Handle null/undefined external IDs - put them at the end
          const aExtId = a.externalId || ''
          const bExtId = b.externalId || ''
          if (!aExtId && bExtId) return 1
          if (aExtId && !bExtId) return -1
          return aExtId.localeCompare(bExtId) * order

        case 'modifiedAt':
          // Date comparison
          const aDate = new Date(a.modifiedAt).getTime()
          const bDate = new Date(b.modifiedAt).getTime()
          return (aDate - bDate) * order

        case 'name':
          return a.name.localeCompare(b.name) * order

        case 'policyCount':
          return (a.policyCount - b.policyCount) * order

        default:
          return 0
      }
    })
  }

  // Check if role can be deleted (system roles and default account roles cannot be deleted)
  canDeleteRole(role: RoleManagementItem | null): boolean {
    if (!role) return false
    return !role.isSystemRole
  }

  // Check if role can be edited (system roles can now be edited for external ID)
  canEditRole(role: RoleManagementItem | null): boolean {
    if (!role) return false
    // All roles can now be edited - system roles have limited editing (external ID only)
    return true
  }

  // Check if role is a system role
  isSystemRole(role: RoleManagementItem): boolean {
    return role.isSystemRole || false
  }

  // Get policies for the selected role
  getRolePolicies(): PolicyInfo[] {
    if (!this.selectedRoleDetails?.policies) {
      return []
    }

    return this.selectedRoleDetails.policies.map((policy, index) => ({
      accessLevel: policy.accessLevel,
      allApps: policy.allApps || false,
      appIds: policy.appIds,
      appNames: this.getAppNamesFromIds(policy.appIds),
      policyName: `Policy ${index + 1}`,
    }))
  }

  // Convert app IDs to app names for display
  private getAppNamesFromIds(appIds: string[] | null): string[] {
    if (!appIds || appIds.length === 0) {
      return []
    }

    const names = appIds
      .map((appId) => {
        const app = this.applications.find((a) => a.appId === appId)
        return app ? app.name : null // Return null instead of ID
      })
      .filter((name) => name !== null) // Filter out null values

    // If we couldn't find names for all IDs, return empty array (hide the whole line)
    return names.length === appIds.length ? names : []
  }

  // Refresh application names in already loaded role policies
  private refreshRolePolicyAppNames(): void {
    // This method forces a re-render of the policies sidebar by triggering change detection
    // The getRolePolicies() method will be called again and will use the now-loaded applications
    if (this.selectedRoleDetails) {
      // Trigger change detection by creating a new reference
      this.selectedRoleDetails = { ...this.selectedRoleDetails }
    }
  }

  // Get formatted policy description
  getPolicyDescription(policy: PolicyInfo): string {
    const accessDisplay = this.getAccessLevelDisplay(policy.accessLevel)

    if (policy.allApps) {
      return `${accessDisplay} access to all applications`
    }

    if (!policy.appNames || policy.appNames.length === 0) {
      return `${accessDisplay} access`
    }

    if (policy.appNames.length === 1) {
      return `${accessDisplay} access to ${policy.appNames[0]}`
    }

    return `${accessDisplay} access to ${policy.appNames.length} applications`
  }

  // Get policy description components for template rendering with tooltip support
  getPolicyDescriptionComponents(policy: PolicyInfo): {
    accessLevel: string
    prefix: string
    applicationsText: string
    suffix: string
    showTooltip: boolean
    tooltipText: string
  } {
    const accessDisplay = this.getAccessLevelDisplay(policy.accessLevel)

    if (policy.allApps) {
      return {
        accessLevel: accessDisplay,
        prefix: ' access to ',
        applicationsText: 'all applications',
        suffix: '',
        showTooltip: false,
        tooltipText: '',
      }
    }

    if (!policy.appNames || policy.appNames.length === 0) {
      return {
        accessLevel: accessDisplay,
        prefix: ' access',
        applicationsText: '',
        suffix: '',
        showTooltip: false,
        tooltipText: '',
      }
    }

    if (policy.appNames.length === 1) {
      return {
        accessLevel: accessDisplay,
        prefix: ' access to ',
        applicationsText: policy.appNames[0],
        suffix: '',
        showTooltip: false,
        tooltipText: '',
      }
    }

    // Multiple applications - show tooltip
    return {
      accessLevel: accessDisplay,
      prefix: ' access to ',
      applicationsText: `${policy.appNames.length} applications`,
      suffix: '',
      showTooltip: true,
      tooltipText: policy.appNames.map((name) => `<span class="tooltip-badge">${name}</span>`).join(''),
    }
  }

  deleteRole() {
    if (!this.selectedRole) return

    this.roleToDelete = this.selectedRole
    this.deleteRoleDialog.open()
  }

  confirmDeleteRole() {
    if (!this.roleToDelete) return

    this.apiService.deleteRole(this.accountService.accountId, this.roleToDelete.id).subscribe(
      () => {
        this.deleteRoleDialog.close()
        this.closeSidePanel()
        this.loadData() // Refresh the list
      },
      handleArpioError(window.location.href, this.apiService, this.uiService, {
        errorContext: { dialogId: 'delete-role-confirm' },
      })
    )
  }

  cancelDeleteRole() {
    this.roleToDelete = null
    this.deleteRoleDialog.close()
  }

  // Tooltip methods for delay and hover functionality
  private tooltipHideTimer: any
  currentTooltipContent: string = ''

  keepTooltipVisible() {
    if (this.tooltipHideTimer) {
      clearTimeout(this.tooltipHideTimer)
      this.tooltipHideTimer = null
    }
  }

  hideTooltipDelayed() {
    this.tooltipHideTimer = setTimeout(() => {
      // Logic to hide tooltip - this depends on the tooltip implementation
      // For now, we'll let the PrimeNG tooltip handle it naturally
    }, 200)
  }

  setTooltipContent(content: string) {
    this.currentTooltipContent = content
  }

  // Get tooltip text for additional applications (applications beyond the first 3 displayed)
  getAdditionalApplicationsTooltip(role: RoleManagementItem): string {
    if (role.applications.length <= 3) return ''
    const additionalApplications = role.applications.slice(3)
    return additionalApplications.map((app) => `<span class="tooltip-badge">${app}</span>`).join('')
  }

  // Get tooltip content for access level badges
  getAccessLevelTooltip(accessLevelInfo: AccessLevelInfo): string {
    return generateAccessLevelTooltip(accessLevelInfo.applications)
  }
}
